import { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { connect } from 'react-redux';
import { canDownloadEntities } from 'containers/decorators';
import { gql } from 'utils/ajax';
import { Checkbox } from 'components/forms';
import Row from 'components/UIKit/Flex/Row';
import Column from 'components/UIKit/Flex/Column';
import Button from 'components/UIKit/Button/Button';
import DownloadIcon from 'components/icons/DownloadIcon';
import { Spinner } from 'components/UIKit/Spinner';
import { setMessage } from 'ducks/message';
import { getSubmittableFileDefinitions } from 'utils/dictionaryHelpers';
import { SUBMITTABLE_DATA_FILES_ORDER } from 'constants/constants';

class DataCategoryDownloadContainer extends Component {
  static propTypes = {
    closeModal: PropTypes.func,
    downloadEntitiesByIds: PropTypes.func,
    setIsSpinning: PropTypes.func,
  };

  // $FlowIgnore
  state: {
    dropdownActive: boolean,
    entitiesToDownload: Array<string>,
    fileFormat: string,
    idsByEntity: Object,
    isFetching: boolean,
    entitiesByCategory: {
      clinical: Array<string>,
      biospecimen: Array<string>,
      annotation: Array<string>,
      submittable_data_files: Array<string>,
    },
  } = {
    entitiesToDownload: [],
    idsByEntity: {},
    isFetching: false,
    entitiesByCategory: {
      clinical: [],
      biospecimen: [],
      annotation: [],
      submittable_data_files: [],
    },
  };

  componentDidMount() {
    const { defs } = this.props;
    if (defs.length) {
      const entitiesByCategory: {
        clinical: Array<string>,
        biospecimen: Array<string>,
        submittable_data_files: Array<string>,
        annotation: Array<string>
      } = this.setEntitiesByCategory(defs);
      this.setIds([
        ...entitiesByCategory.clinical,
        ...entitiesByCategory.biospecimen,
        ...entitiesByCategory.submittable_data_files,
        ...entitiesByCategory.annotation,
      ]);
    }
  }

  componentDidUpdate(prevProps): void {
    const { defs } = prevProps;
    if (this.props.defs.length && this.props.defs !== defs) {
      const entitiesByCategory: {
        clinical: Array<string>,
        biospecimen: Array<string>,
        submittable_data_files: Array<string>,
        annotation: Array<string>
      } = this.setEntitiesByCategory(nextProps.defs);
      this.setIds([
        ...entitiesByCategory.clinical,
        ...entitiesByCategory.biospecimen,
        ...entitiesByCategory.submittable_data_files,
        ...entitiesByCategory.annotation,
      ]);
    }
  }

  setEntitiesByCategory(defs: Array<Object>): Object {
    const entitiesByCategory = defs.reduce((acc, n) => ({
      ...acc,
      [n.category]: [...(acc[n.category] || []), n.id],
    }), {
      annotation: [],
      biospecimen: [],
      clinical: [],
      submittable_data_files: [],
    });
    // 'clinical' itself is an old node for legacy data, don't dl it
    entitiesByCategory.clinical = _.difference(entitiesByCategory.clinical || [], ['clinical']);
    entitiesByCategory.submittable_data_files = getSubmittableFileDefinitions({ defs })
      .map(i => i.id)
      .sort((a, b) => SUBMITTABLE_DATA_FILES_ORDER.indexOf(a)
        - SUBMITTABLE_DATA_FILES_ORDER.indexOf(b));

    // annotations are category: notation but don't want to dl
    // the read_group_qc that are also that category
    entitiesByCategory.annotation = ['annotation'];
    this.setState({ entitiesByCategory });
    return entitiesByCategory;
  }

  async setIds(entities: Array<string>) {
    const { program, project } = this.props.router.params;
    this.setState({ isFetching: true });
    const { data } = await gql({
      variables: {
        projectId: project ? `${program}-${project}` : null,
      },
      query: `
        query EntitiesIDQuery(
          $projectId: [String]
        ) {
          ${entities.map(e => `${e} (project_id: $projectId first: 0) { id }`).join()}
        }
    `,
    });
    this.setState({
      isFetching: false,
      idsByEntity: _.reduce(data, (acc, d, e) => ({ ...acc, [e]: d.map(item => item.id) }), {}),
    });
  }

  // $FlowIgnore
  toggleEntitiesToDownload: Function = (entities: Array<string>) => {
    const { entitiesToDownload } = this.state;
    const intersection = _.intersection(entitiesToDownload, entities);
    if (intersection.length === entities.length) {
      this.setState({ entitiesToDownload: _.difference(entitiesToDownload, entities) });
    } else {
      this.setState({ entitiesToDownload: _.uniq([...entitiesToDownload, ...entities]) });
    }
  };

  render(): React.Element {
    const {
      closeModal,
      dispatch,
      downloadEntitiesByIds,
      setIsSpinning,
    } = this.props;
    const {
      entitiesToDownload,
      entitiesByCategory,
      idsByEntity,
      isFetching,
    } = this.state;

    const experimantalDataEntitiesWithIds = entitiesByCategory.submittable_data_files
      .filter(e => (idsByEntity[e] || []).length);

    const styles = {
      li: {
        margin: '0 0 3px 0',
      },
      spinner: {
        position: 'absolute',
        top: 0,
        left: 0,
        height: '100%',
        width: '100%',
        zIndex: 20,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        pointerEvents: 'none',
        backgroundColor: 'rgba(0, 0, 0, 0.15)',
        transition: 'opacity 0.35s ease',
      },
    };

    return (
      <Column
        style={{
          maxHeight: 'calc(90vh - 4.8rem)',
          overflowY: 'auto',
        }}
        >
        <span style={{ padding: '5px 25px 10px 20px' }}>
          <div
            style={{
              ...styles.spinner,
            }}
            >
            {isFetching ? <Spinner /> : null}
          </div>
          <ul style={{ listStyle: 'none', paddingLeft: '0' }}>
            <li style={styles.li}>
              <Checkbox
                labelText={`Clinical (${entitiesByCategory.clinical.reduce((acc, e) => [...acc, ...idsByEntity[e] || []], []).length})`}
                disabled={entitiesByCategory.clinical
                  .reduce((acc, e) => [
                    ...acc,
                    ...idsByEntity[e] || [],
                  ], [])
                  .length === 0}
                onChange={() => this.toggleEntitiesToDownload(entitiesByCategory.clinical || [])}
                />
            </li>
            <li style={styles.li}>
              <Checkbox
                labelText={`Biospecimen (${entitiesByCategory.biospecimen.reduce((acc, e) => [...acc, ...idsByEntity[e] || []], []).length})`}
                disabled={entitiesByCategory.biospecimen
                  .reduce((acc, e) => [
                    ...acc,
                    ...idsByEntity[e] || [],
                  ], [])
                  .length === 0}
                onChange={() => this.toggleEntitiesToDownload(entitiesByCategory.biospecimen || [])}
                />
            </li>
            <li style={styles.li}>
              <Checkbox
                checked={entitiesToDownload.length !== 0
                  && _.intersection(
                    experimantalDataEntitiesWithIds,
                    entitiesToDownload,
                  ).length === experimantalDataEntitiesWithIds.length}
                labelText={`Submittable Data Files (${entitiesByCategory.submittable_data_files
                  .reduce((acc, e) => [
                    ...acc, ...idsByEntity[e] || [],
                  ], [])
                  .length})`}
                disabled={entitiesByCategory.submittable_data_files
                  .reduce((acc, e) => [
                    ...acc, ...idsByEntity[e] || [],
                  ], [])
                  .length === 0}
                onChange={() => this.toggleEntitiesToDownload(experimantalDataEntitiesWithIds)}
                />
            </li>
            { (entitiesByCategory.submittable_data_files || []).map(t => (
              <li key={t} style={styles.li}>
                <Checkbox
                  key={t}
                  labelText={`${t.replace(/_/g, ' ')} (${(idsByEntity[t] || []).length})`}
                  onChange={() => this.toggleEntitiesToDownload([t])}
                  disabled={
                    entitiesToDownload.filter(e => entitiesByCategory.submittable_data_files
                      .includes(e)).length === entitiesByCategory.submittable_data_files.length

                      || (idsByEntity[t] || []).length === 0
                  }
                  checked={entitiesToDownload.includes(t)}
                  style={{
                    marginLeft: '20px',
                    textTransform: 'capitalize',
                  }}
                  />
              </li>
            )) }
            <li style={styles.li}>
              <Checkbox
                labelText={`Annotations (${entitiesByCategory.annotation.reduce((acc, e) => [...acc, ...idsByEntity[e] || []], []).length})`}
                disabled={entitiesByCategory.annotation
                  .reduce((acc, e) => [
                    ...acc,
                    ...idsByEntity[e] || [],
                  ], [])
                  .length === 0}
                onChange={() => this.toggleEntitiesToDownload(entitiesByCategory.annotation || [])}
                />
            </li>
          </ul>
        </span>
        <Row
          style={{
            flex: 1,
            padding: '10px 0',
            borderTop: '1px solid #ccc',
            justifyContent: 'center',
          }}
          >
          <Button
            disabled={isFetching || entitiesToDownload.length < 1}
            onClick={async () => {
              closeModal();
              setIsSpinning(true);
              try {
                await downloadEntitiesByIds(
                  {
                    ids: entitiesToDownload.reduce((acc, e) => [...acc, ...idsByEntity[e]], []),
                    format: 'tsv',
                  },
                );
              } catch (err) {
                dispatch(setMessage('Error downloading project data'));
              }
              setIsSpinning(false);
            }}
            >
            <DownloadIcon />
            Download TSV
          </Button>
          <Button
            disabled={isFetching || entitiesToDownload.length < 1}
            onClick={async () => {
              closeModal();
              setIsSpinning(true);
              try {
                await downloadEntitiesByIds(
                  {
                    ids: entitiesToDownload.reduce((acc, e) => [...acc, ...idsByEntity[e]], []),
                    format: 'json',
                  },
                );
              } catch (err) {
                dispatch(setMessage('Error downloading project data'));
              }
              setIsSpinning(false);
            }}
            >
            <DownloadIcon />
            Download JSON
          </Button>
        </Row>
      </Column>
    );
  }
}

export default connect(
  state => ({
    defs: state.entityDefinitions.defs,
  }),
)(canDownloadEntities(DataCategoryDownloadContainer));
